/**
 * SSOT — צילום נוסעים לפני validation / persist / booking pipeline.
 */

import { normalizePassengerRoomArrays } from "./buildPassengersForGemonixBooking";
import {
  resolveHotelPassengerFormRooms,
  summarizeHotelPassengerOccupancy,
} from "./resolveHotelPassengerFormRooms";
import { isEnglishOnlyPersonName } from "./passengers/englishOnlyPersonName";

function deepClone(x) {
  return JSON.parse(JSON.stringify(x));
}

function nonEmptyStr(v) {
  return v != null && String(v).trim() !== "";
}

function hasValidEnglishPersonName(v) {
  return nonEmptyStr(v) && isEnglishOnlyPersonName(v);
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

const FIELD_LABEL_HE = {
  firstname: "שם פרטי",
  lastname: "שם משפחה",
  cell: "טלפון",
  email: "דוא״ל",
  roomCountMismatch: "מספר חדרים בהזמנה",
  emptyPassengers: "פרטי נוסעים",
  noRooms: "מבנה חדרים",
};

/**
 * תבנית חדרים מהגדרת החיפוש (Rooms) — לא משתנה במחיר/חבילה.
 * @param {unknown} rooms
 * @returns {object[][]}
 */
export function buildInitialHotelPassengerRoomSlots(rooms) {
  /** @type {object[][]} */
  const RoomData = [];
  const normalizedRooms = resolveHotelPassengerFormRooms(rooms);
  if (!normalizedRooms.length) return RoomData;

  const adultsCountPerRoom = normalizedRooms.map((room) => room.AdultsCount);
  const kidsCountPerRoom = normalizedRooms.map((room) =>
    room.KidsAges ? room.KidsAges.length : 0
  );
  const infantsCountPerRoom = normalizedRooms.map(
    (room) => room.InfantsCount ?? 0
  );

  for (let i = 0; i < normalizedRooms.length; i++) {
    const roomArray = [];

    if (i === 0) {
      roomArray.push({
        firstname: null,
        lastname: null,
        cell: null,
        email: null,
        isKid: false,
      });
    } else {
      roomArray.push({ firstname: null, lastname: null, isKid: false });
    }

    for (let j = 0; j < adultsCountPerRoom[i] - 1; j++) {
      roomArray.push({
        firstname: null,
        lastname: null,
        isKid: false,
      });
    }

    for (let k = 0; k < kidsCountPerRoom[i]; k++) {
      roomArray.push({
        firstname: null,
        lastname: null,
        isKid: true,
      });
    }

    for (let k = 0; k < infantsCountPerRoom[i]; k++) {
      roomArray.push({
        firstname: null,
        lastname: null,
        isKid: false,
        isInfant: true,
      });
    }

    RoomData.push(roomArray);
  }

  return RoomData;
}

/**
 * כשנפתחים/משתנים חדרים — שומר קלט משתמש בשכבות תואמות.
 * @param {unknown} prev
 * @param {object[][]} desiredTemplate
 */
export function mergeHotelPassengerSlotsPreserveUser(prev, desiredTemplate) {
  if (!Array.isArray(desiredTemplate) || desiredTemplate.length === 0) {
    return prev;
  }
  const prevArr = normalizePassengerRoomArrays(prev);
  const out = [];
  for (let i = 0; i < desiredTemplate.length; i++) {
    const tmplRow = desiredTemplate[i];
    const oldRow = Array.isArray(prevArr[i]) ? prevArr[i] : [];
    const row = tmplRow.map((slotTpl, j) => {
      const old = oldRow[j];
      if (old && typeof old === "object") {
        return { ...slotTpl, ...old };
      }
      return { ...slotTpl };
    });
    out.push(row);
  }
  return out;
}

function countSlotsInPassengerRoomsInput(snapshot) {
  if (snapshot.mode === "dynamic") {
    const arr = snapshot.passengerRoomsInput || [];
    return arr.reduce((sum, room) => {
      const a = Array.isArray(room?.adults) ? room.adults.length : 0;
      const k = Array.isArray(room?.kids) ? room.kids.length : 0;
      const inf = Array.isArray(room?.infants) ? room.infants.length : 0;
      return sum + a + k + inf;
    }, 0);
  }
  const norm = snapshot.passengerRoomsInput || [];
  return norm.reduce((sum, room) => {
    const row = Array.isArray(room) ? room : room ? [room] : [];
    return sum + row.length;
  }, 0);
}

function countSlotsInRoomRows(roomRows) {
  if (!Array.isArray(roomRows)) return 0;
  return roomRows.reduce((sum, room) => {
    const row = Array.isArray(room) ? room : room ? [room] : [];
    return sum + row.length;
  }, 0);
}

/**
 * SSOT — מיזוג passengerInfo עם תבנית תפוסה (מבוגרים/ילדים/תינוקות לפי חדר).
 * @param {unknown} passengerInfo
 * @param {unknown} rawRooms
 */
export function resolveHotelPassengerSlotsFromOccupancy(passengerInfo, rawRooms) {
  const template = buildInitialHotelPassengerRoomSlots(rawRooms);
  const rawNorm = normalizePassengerRoomArrays(passengerInfo);
  const merged = mergeHotelPassengerSlotsPreserveUser(
    rawNorm.length ? passengerInfo : null,
    template
  );
  const resolved = Array.isArray(merged) ? merged : [];
  const occupancy = summarizeHotelPassengerOccupancy(rawRooms);
  const rawSlotCount = countSlotsInRoomRows(rawNorm);
  const resolvedSlotCount = countSlotsInRoomRows(resolved);

  return {
    template,
    resolved,
    occupancy,
    rawPassengerInfoRoomCount: rawNorm.length,
    rawSlotCount,
    resolvedSlotCount,
    paddedMissingSlotsCount: Math.max(0, resolvedSlotCount - rawSlotCount),
    sourceUsed:
      "occupancy:resolveHotelPassengerFormRooms→buildInitialHotelPassengerRoomSlots→mergeHotelPassengerSlotsPreserveUser",
  };
}

/** @param {Record<string, unknown>} payload */
export function logHotelPassengerSsotResolveTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  // eslint-disable-next-line no-console
  console.info("[HOTEL_PASSENGER_SSOT_RESOLVE_TRACE]", payload);
}

function validateHotelPassengerSlot(pax, roomIndex, passengerIndex) {
  const fields = {
    firstname: hasValidEnglishPersonName(pax?.firstname),
    lastname: hasValidEnglishPersonName(pax?.lastname),
  };
  /** @type {Array<{ roomIndex: number, role: string, passengerIndex: number, field: string }>} */
  const missing = [];

  const isLeadContact =
    roomIndex === 0 &&
    passengerIndex === 0 &&
    !pax?.isKid &&
    !pax?.isInfant;

  if (isLeadContact) {
    fields.cell = nonEmptyStr(pax?.cell);
    fields.email = nonEmptyStr(pax?.email);
    if (!fields.cell) {
      missing.push({
        roomIndex,
        role: "adult",
        passengerIndex,
        field: "cell",
      });
    }
    if (!fields.email) {
      missing.push({
        roomIndex,
        role: "adult",
        passengerIndex,
        field: "email",
      });
    }
  }

  if (!fields.firstname) {
    missing.push({
      roomIndex,
      role: pax?.isInfant ? "infant" : pax?.isKid ? "kid" : "guest",
      passengerIndex,
      field: "firstname",
    });
  }
  if (!fields.lastname) {
    missing.push({
      roomIndex,
      role: pax?.isInfant ? "infant" : pax?.isKid ? "kid" : "guest",
      passengerIndex,
      field: "lastname",
    });
  }

  return {
    fields,
    missing,
    passengerRole: pax?.isInfant
      ? "infant"
      : pax?.isKid
        ? "kid"
        : isLeadContact
          ? "adult"
          : "guest",
  };
}

function summarizePassengerRoomsForTrace(snapshot) {
  if (snapshot.mode === "dynamic") {
    const arr = Array.isArray(snapshot.passengerRoomsInput)
      ? snapshot.passengerRoomsInput
      : [];
    return arr.map((room, ri) => ({
      roomIndex: ri,
      adults: Array.isArray(room?.adults) ? room.adults.length : 0,
      kids: Array.isArray(room?.kids) ? room.kids.length : 0,
      infants: Array.isArray(room?.infants) ? room.infants.length : 0,
    }));
  }
  const norm = Array.isArray(snapshot.passengerRoomsInput)
    ? snapshot.passengerRoomsInput
    : [];
  return norm.map((room, ri) => ({
    roomIndex: ri,
    guests: Array.isArray(room) ? room.length : room ? 1 : 0,
  }));
}

/**
 * @param {{
 *   isDynamicPackage: boolean,
 *   passengerInfo: unknown,
 *   roomsAndPaxInfo: unknown,
 *   rooms: unknown,
 *   offerId?: string|null,
 *   selectedRoomId?: string|null,
 *   roomCode?: string|null,
 * }} opts
 */
export function computeBookingPassengerSnapshot(opts) {
  const { isDynamicPackage, passengerInfo, roomsAndPaxInfo } = opts;
  const rooms = opts.rooms;
  const expectedRoomCount = Array.isArray(rooms) ? rooms.length : 0;

  /** @type {Array<{ roomIndex: number, role: string, passengerIndex: number, field: string, detail?: unknown }>} */
  const missingRequiredFieldsByPassenger = [];
  /** @type {object[]} */
  const requiredFieldsStatus = [];

  if (isDynamicPackage) {
    const snapshotSource = "SelectedResult.roomsAndPaxInfo";
    const arr = asArray(roomsAndPaxInfo);

    if (arr.length === 0) {
      missingRequiredFieldsByPassenger.push({
        roomIndex: -1,
        role: "structure",
        passengerIndex: -1,
        field: "noRooms",
      });
    }

    arr.forEach((room, roomIndex) => {
      const adults = asArray(room?.adults);
      const kids = asArray(room?.kids);
      const infants = asArray(room?.infants);

      adults.forEach((adult, i) => {
        const fields = {
          firstname: hasValidEnglishPersonName(adult?.firstname),
          lastname: hasValidEnglishPersonName(adult?.lastname),
          cell: nonEmptyStr(adult?.cell),
          email: nonEmptyStr(adult?.email),
        };
        requiredFieldsStatus.push({
          roomIndex,
          passengerRole: "adult",
          passengerIndex: i,
          fields,
        });
        for (const k of ["firstname", "lastname", "cell", "email"]) {
          if (!fields[k]) {
            missingRequiredFieldsByPassenger.push({
              roomIndex,
              role: "adult",
              passengerIndex: i,
              field: k,
            });
          }
        }
      });

      kids.forEach((kid, i) => {
        const fields = {
          firstname: hasValidEnglishPersonName(kid?.firstname),
          lastname: hasValidEnglishPersonName(kid?.lastname),
        };
        requiredFieldsStatus.push({
          roomIndex,
          passengerRole: "kid",
          passengerIndex: i,
          fields,
        });
        for (const k of ["firstname", "lastname"]) {
          if (!fields[k]) {
            missingRequiredFieldsByPassenger.push({
              roomIndex,
              role: "kid",
              passengerIndex: i,
              field: k,
            });
          }
        }
      });

      infants.forEach((infant, i) => {
        const fields = {
          firstname: hasValidEnglishPersonName(infant?.firstname),
          lastname: hasValidEnglishPersonName(infant?.lastname),
        };
        requiredFieldsStatus.push({
          roomIndex,
          passengerRole: "infant",
          passengerIndex: i,
          fields,
        });
        for (const k of ["firstname", "lastname"]) {
          if (!fields[k]) {
            missingRequiredFieldsByPassenger.push({
              roomIndex,
              role: "infant",
              passengerIndex: i,
              field: k,
            });
          }
        }
      });
    });

    const persistDynamic = deepClone(arr);
    const passengerRoomsInput = persistDynamic;

    const snapshot = {
      snapshotSource,
      mode: "dynamic",
      isComplete: missingRequiredFieldsByPassenger.length === 0,
      passengerRoomsInput,
      inputKind: /** @type {const} */ ("dynamic-pax-structured"),
      missingRequiredFieldsByPassenger,
      requiredFieldsStatus,
      persistForOrder: {
        passengerInfo:
          passengerInfo != null ? deepClone(passengerInfo) : null,
        roomsAndPaxInfo: persistDynamic,
      },
    };

    const visibleSlots = countSlotsInPassengerRoomsInput(snapshot);
    snapshot.visibleSlotCount = visibleSlots;
    snapshot.validationSlotCount = requiredFieldsStatus.length;
    snapshot.visibleVsValidationMismatch =
      visibleSlots !== requiredFieldsStatus.length;
    snapshot.validationBookingDigest = JSON.stringify(passengerRoomsInput);
    snapshot.validationVsBookingMismatch = false;

    return snapshot;
  }

  const slotResolution = resolveHotelPassengerSlotsFromOccupancy(
    passengerInfo,
    rooms
  );
  const resolved = slotResolution.resolved;
  const snapshotSource = slotResolution.sourceUsed;

  logHotelPassengerSsotResolveTrace({
    offerId: opts.offerId ?? null,
    selectedRoomId: opts.selectedRoomId ?? null,
    roomCode: opts.roomCode ?? null,
    passengerInfoRoomCount: slotResolution.rawPassengerInfoRoomCount,
    passengerInfoSlotCount: slotResolution.rawSlotCount,
    resolvedAdults: slotResolution.occupancy.adults,
    resolvedChildren: slotResolution.occupancy.children,
    resolvedTotalGuests: slotResolution.occupancy.totalGuestsResolved,
    visibleSlotCount: slotResolution.resolvedSlotCount,
    validationSlotCount: null,
    bookingSlotCount: slotResolution.resolvedSlotCount,
    paddedMissingSlotsCount: slotResolution.paddedMissingSlotsCount,
    sourceUsed: snapshotSource,
  });

  if (expectedRoomCount > 0 && resolved.length === 0) {
    missingRequiredFieldsByPassenger.push({
      roomIndex: -1,
      role: "structure",
      passengerIndex: -1,
      field: "emptyPassengers",
    });
  } else if (
    expectedRoomCount > 0 &&
    resolved.length !== expectedRoomCount
  ) {
    missingRequiredFieldsByPassenger.push({
      roomIndex: -1,
      role: "structure",
      passengerIndex: -1,
      field: "roomCountMismatch",
      detail: { expected: expectedRoomCount, actual: resolved.length },
    });
  }

  resolved.forEach((room, roomIndex) => {
    const row = Array.isArray(room) ? room : room ? [room] : [];
    row.forEach((pax, passengerIndex) => {
      const { fields, missing, passengerRole } = validateHotelPassengerSlot(
        pax,
        roomIndex,
        passengerIndex
      );
      requiredFieldsStatus.push({
        roomIndex,
        passengerRole,
        passengerIndex,
        fields,
      });
      missing.forEach((m) => missingRequiredFieldsByPassenger.push(m));
    });
  });

  const persistHotel = deepClone(resolved);
  const passengerRoomsInput = persistHotel;

  const snapshot = {
    snapshotSource,
    mode: "hotel",
    isComplete: missingRequiredFieldsByPassenger.length === 0,
    passengerRoomsInput,
    inputKind: /** @type {const} */ ("nested-room-arrays"),
    missingRequiredFieldsByPassenger,
    requiredFieldsStatus,
    persistForOrder: {
      passengerInfo: persistHotel,
      roomsAndPaxInfo: null,
    },
    slotResolution,
  };

  const visibleSlots = countSlotsInPassengerRoomsInput(snapshot);
  const validationSlots = requiredFieldsStatus.length;
  snapshot.visibleSlotCount = visibleSlots;
  snapshot.validationSlotCount = validationSlots;
  snapshot.bookingSlotCount = countSlotsInRoomRows(passengerRoomsInput);
  snapshot.visibleVsValidationMismatch = visibleSlots !== validationSlots;
  snapshot.validationBookingDigest = JSON.stringify(passengerRoomsInput);
  snapshot.validationVsBookingMismatch =
    snapshot.bookingSlotCount !== validationSlots;

  logHotelPassengerSsotResolveTrace({
    offerId: opts.offerId ?? null,
    selectedRoomId: opts.selectedRoomId ?? null,
    roomCode: opts.roomCode ?? null,
    passengerInfoRoomCount: slotResolution.rawPassengerInfoRoomCount,
    passengerInfoSlotCount: slotResolution.rawSlotCount,
    resolvedAdults: slotResolution.occupancy.adults,
    resolvedChildren: slotResolution.occupancy.children,
    resolvedTotalGuests: slotResolution.occupancy.totalGuestsResolved,
    visibleSlotCount: visibleSlots,
    validationSlotCount: validationSlots,
    bookingSlotCount: snapshot.bookingSlotCount,
    paddedMissingSlotsCount: slotResolution.paddedMissingSlotsCount,
    sourceUsed: snapshotSource,
    isComplete: snapshot.isComplete,
  });

  return snapshot;
}

/** @param {ReturnType<typeof computeBookingPassengerSnapshot>} snapshot */
export function logHotelBookVisibleFormStateTrace(snapshot) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;

  const summary = summarizePassengerRoomsForTrace(snapshot);
  const trace = {
    snapshotSource: snapshot.snapshotSource,
    visibleFormSourceName: snapshot.snapshotSource,
    validationSourceName: snapshot.snapshotSource,
    bookingSourceName: snapshot.snapshotSource,
    visiblePassengers: summary,
    validationPassengers: summary,
    bookingPassengers: summary,
    bookingSlotCount: snapshot.bookingSlotCount ?? null,
    paddedMissingSlotsCount:
      snapshot.slotResolution?.paddedMissingSlotsCount ?? null,
    requiredFieldsStatus: snapshot.requiredFieldsStatus,
    missingRequiredFieldsByPassenger: snapshot.missingRequiredFieldsByPassenger,
    visibleVsValidationMismatch: snapshot.visibleVsValidationMismatch,
    validationVsBookingMismatch: snapshot.validationVsBookingMismatch,
    visibleSlotCount: snapshot.visibleSlotCount,
    validationSlotCount: snapshot.validationSlotCount,
    mode: snapshot.mode,
    isComplete: snapshot.isComplete,
  };

  // eslint-disable-next-line no-console
  console.error("[HOTEL_BOOK_VISIBLE_FORM_STATE_TRACE]", trace);
}

export function formatPassengerSnapshotErrorsHe(snapshot, maxLines = 12) {
  const missing = snapshot.missingRequiredFieldsByPassenger || [];
  if (missing.length === 0) return "";

  const lines = [];
  for (const m of missing) {
    if (lines.length >= maxLines) break;
    if (m.field === "roomCountMismatch" && m.detail) {
      lines.push(
        `מספר חדרים בטופס (${m.detail.actual}) לא תואם להזמנה (${m.detail.expected}).`
      );
      continue;
    }
    if (m.field === "emptyPassengers") {
      lines.push("חסרים לחלוטין פרטי נוסעים — נא למלא לפי מספר החדרים.");
      continue;
    }
    if (m.field === "noRooms") {
      lines.push("חסר מבנה חדרים לנוסעים — נא לרענן או לחזור לבחירת חדר.");
      continue;
    }

    const ri = m.roomIndex;
    const roomLabel = ri >= 0 ? `חדר ${ri + 1}` : "הזמנה";
    const roleHe =
      m.role === "adult"
        ? "מבוגר"
        : m.role === "kid"
          ? "ילד"
          : m.role === "infant"
            ? "תינוק"
            : m.role === "guest"
              ? "נוסע"
              : m.role;
    const idxLabel = m.passengerIndex >= 0 ? m.passengerIndex + 1 : "—";
    const fname = FIELD_LABEL_HE[m.field] || m.field;
    lines.push(`${roomLabel}, ${roleHe} ${idxLabel}: חסר ${fname}.`);
  }

  if (missing.length > maxLines) {
    lines.push(`ועוד ${missing.length - maxLines} שדות חסרים…`);
  }

  return lines.join("\n");
}
